import {
  DevEncounterCreature,
  getDevEncounterCreatureSaved,
  proxyDevEncounterCreatureImageUrl,
  saveDevEncounterCreatureImage,
} from 'app/frontend/base/api/devEncounterCreatures'

export const VIEWPORT_SIZE = 200
export const EXPORT_SIZE = 256

export const sampleBackgroundColor = (image: HTMLImageElement) => {
  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return 'rgb(0, 0, 0)'

  ctx.drawImage(image, 0, 0)
  const points = [
    [0, 0],
    [image.naturalWidth - 1, 0],
    [0, image.naturalHeight - 1],
    [image.naturalWidth - 1, image.naturalHeight - 1],
    [Math.floor(image.naturalWidth / 2), 0],
    [Math.floor(image.naturalWidth / 2), image.naturalHeight - 1],
    [0, Math.floor(image.naturalHeight / 2)],
    [image.naturalWidth - 1, Math.floor(image.naturalHeight / 2)],
  ]

  let r = 0
  let g = 0
  let b = 0
  points.forEach(([x, y]) => {
    const [pr, pg, pb] = ctx.getImageData(x, y, 1, 1).data
    r += pr
    g += pg
    b += pb
  })

  const count = points.length
  return `rgb(${Math.round(r / count)}, ${Math.round(g / count)}, ${Math.round(
    b / count,
  )})`
}

export const coverFitTransform = (image: HTMLImageElement) => {
  const scale = Math.max(
    VIEWPORT_SIZE / image.naturalWidth,
    VIEWPORT_SIZE / image.naturalHeight,
  )
  return {
    scale,
    offsetX: (VIEWPORT_SIZE - image.naturalWidth * scale) / 2,
    offsetY: (VIEWPORT_SIZE - image.naturalHeight * scale) / 2,
  }
}

export const loadProxiedCreatureImage = (zoomUrl: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Failed to load ${zoomUrl}`))
    image.src = proxyDevEncounterCreatureImageUrl(zoomUrl)
  })

export const exportCreaturePng = async (
  image: HTMLImageElement,
  options: {
    scale: number
    offsetX: number
    offsetY: number
    backgroundColor: string
  },
) => {
  const canvas = document.createElement('canvas')
  canvas.width = EXPORT_SIZE
  canvas.height = EXPORT_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas unavailable')

  const scaleFactor = EXPORT_SIZE / VIEWPORT_SIZE
  ctx.clearRect(0, 0, EXPORT_SIZE, EXPORT_SIZE)
  ctx.beginPath()
  ctx.arc(EXPORT_SIZE / 2, EXPORT_SIZE / 2, EXPORT_SIZE / 2, 0, Math.PI * 2)
  ctx.closePath()
  ctx.clip()
  ctx.fillStyle = options.backgroundColor
  ctx.fillRect(0, 0, EXPORT_SIZE, EXPORT_SIZE)
  ctx.drawImage(
    image,
    options.offsetX * scaleFactor,
    options.offsetY * scaleFactor,
    image.naturalWidth * options.scale * scaleFactor,
    image.naturalHeight * options.scale * scaleFactor,
  )

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png'),
  )
  if (!blob) throw new Error('Failed to export PNG')
  return blob
}

export const saveDefaultCreatureImage = async (
  encounterId: number,
  creature: DevEncounterCreature,
) => {
  if (!creature.zoomUrl) return

  const image = await loadProxiedCreatureImage(creature.zoomUrl)
  const transform = coverFitTransform(image)
  const blob = await exportCreaturePng(image, {
    ...transform,
    backgroundColor: sampleBackgroundColor(image),
  })
  await saveDevEncounterCreatureImage(encounterId, creature.id, blob)
}

export const saveMissingDefaultCreatureImages = async (
  encounters: {
    id: number
    creatures: DevEncounterCreature[]
  }[],
) => {
  for (const encounter of encounters) {
    const { creatureIds } = await getDevEncounterCreatureSaved(encounter.id)
    const savedIds = new Set(creatureIds)

    for (const creature of encounter.creatures) {
      if (savedIds.has(creature.id) || !creature.zoomUrl) continue
      await saveDefaultCreatureImage(encounter.id, creature)
    }
  }
}
